import { useI18n } from "vue-i18n";
import { readsOnly } from "@/shared/i18n/agent-tools";
import { agentsMessages } from "./agents";

/**
 * THE ONE DOOR into the agents' dictionary — and the four answers the chat needs.
 *
 * Same shape as the inbox's and the channels': one helper instead of the same
 * `useI18n({ useScope: "local", … })` incantation repeated in twenty components.
 * What it adds beyond `t` are the handful of things that used to be plain
 * functions with Portuguese written into them — modules with no locale and no
 * way to ask for one.
 *
 * `locale` comes back for `Intl`: the clock under a bubble and the token count
 * beside it were formatted with `"pt-BR"` hardcoded, which is a second place
 * deciding what language the panel is in.
 */
export function useAgentsI18n() {
	const { t, te, locale } = useI18n({
		useScope: "local",
		inheritLocale: true,
		messages: agentsMessages,
	});

	/**
	 * What a tool is called, in words.
	 *
	 * A view over names the platform publishes at `/v3/agents/tools`, so this adds
	 * no capability and no coupling: a tool this dictionary has never heard of
	 * falls back to its own name with the underscores removed, which is worse than
	 * a label and better than `write_structure` on the screen.
	 */
	function toolLabel(name: string, input?: string): string {
		if (readsOnly(name, input)) return t(`toolRead.${name}`);
		// The family is spelled INSIDE the call on purpose: `verify/keys.mjs` reads
		// the static head of a computed key, and a key assembled into a variable
		// first would read as twenty-three dead entries.
		return te(`tool.${name}`) ? t(`tool.${name}`) : name.replace(/_/g, " ");
	}

	/**
	 * How long ago, in words — and COMPACT, for the same reason the inbox's is.
	 *
	 * These four steps sit in a session row inside a 24rem dock, next to a title
	 * that needs every pixel; `formatRelative` would write "45 minutes ago" there.
	 * The shape stays and the unit is translated.
	 */
	function ago(iso: string): string {
		const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
		if (minutes < 1) return t("sessions.ago.now");
		if (minutes < 60) return t("sessions.ago.minutes", { n: minutes });
		const hours = Math.floor(minutes / 60);
		if (hours < 24) return t("sessions.ago.hours", { n: hours });
		return t("sessions.ago.days", { n: Math.floor(hours / 24) });
	}

	/**
	 * The hour a bubble was written, in the language the panel is being read in.
	 *
	 * Epoch millis or an ISO string: a live bubble carries `Date.now()` and a
	 * reopened one carries what the server archived, and both are the same fact.
	 */
	function clock(at: number | string): string {
		return new Date(at).toLocaleTimeString(locale.value, {
			hour: "2-digit",
			minute: "2-digit",
		});
	}

	/** A count with the reader's own thousands separator. */
	function count(value: number): string {
		return value.toLocaleString(locale.value);
	}

	return { t, te, locale, toolLabel, ago, clock, count };
}
